export const M5_UNIT_8 = {
  id: "m5-u8-tools",
  title: "Unit 8: Tools & DIY",
  topic: "Talking about tools, repairs, and DIY projects",
  iconType: "wrench",
  grammarImage: "/grammar/grammar-m5-u8.jpg",
  grammarText: "This unit teaches vocabulary for tools, equipment, and materials used in everyday repairs and DIY projects. You'll learn words for basic tools (hammer, screwdriver, drill, saw), fasteners (nails, screws, bolts), and materials (wood, metal, paint). We combine this vocabulary with grammar you already know: past simple (I used, fixed, built, broke), could/couldn't (I couldn't open it without scissors), and questions (What did you use? Did you need a hammer?). This vocabulary is essential for talking about furniture assembly, home repairs, and DIY projects—situations everyone encounters in daily life.",

  sets: [
    // ===================================================================
    // SET 1: DISCOVERING BASIC TOOLS (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u8-s1",
      title: "Set 1: Basic Tools",
      questions: [
        {
          type: "match",
          q: "Match the tool to its use:",
          pairs: [
            { left: "Hammer", right: "Hit nails into wood" },
            { left: "Screwdriver", right: "Turn screws" },
            { left: "Scissors", right: "Cut paper/fabric" }
          ],
          a: "completed",
          successtip: "✓ Three essential tools! Hammer 🔨, Screwdriver 🪛, Scissors ✂️"
        },
        {
          type: "mc",
          q: "I needed to hang a picture, so I used a ___ to put the nail in the wall.",
          options: ["hammer", "screwdriver", "scissors"],
          a: "hammer",
          successtip: "✓ HAMMER! Hit nails! Classic home task! 🔨🖼️"
        },
        {
          type: "input",
          q: "I couldn't open the package, so I used ___ to cut it. (cutting tool)",
          a: "scissors",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["a knife", "knife"] },
          successtip: "✓ SCISSORS! Or knife! Cutting tools! ✂️📦"
        },
        {
          type: "order",
          q: "Traduce: Usé un destornillador para armar los muebles.",
          words: ["I", "used", "a", "screwdriver", "to", "assemble", "the", "furniture"],
          a: "I used a screwdriver to assemble the furniture",
          tip: "Screwdriver = destornillador! Assemble = armar/ensamblar!",
          successtip: "✓ SCREWDRIVER! Furniture assembly essential! 🪛🪑"
        },
        {
          type: "match",
          q: "More essential tools:",
          pairs: [
            { left: "Drill", right: "Make holes" },
            { left: "Saw", right: "Cut wood/metal" },
            { left: "Wrench", right: "Tighten/loosen bolts" }
          ],
          a: "completed",
          tip: "Drill = taladro, Saw = sierra, Wrench = llave inglesa!",
          successtip: "✓ Power tools! Drill 🔩, Saw 🪚, Wrench 🔧"
        },
        {
          type: "mc",
          q: "STORY: 'I wanted to hang a shelf, so I used a ___ to make holes in the wall for the screws.'",
          options: ["drill", "hammer", "saw"],
          a: "drill",
          tip: "Drill makes holes! Essential for wall mounting!",
          successtip: "✓ DRILL! Taladro! Makes holes in walls! 🔩"
        },
        {
          type: "input",
          q: "I needed to cut a piece of wood, so I used a ___.",
          a: "saw",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Saw = sierra! Cuts wood!",
          successtip: "✓ SAW! Sierra! Cuts wood/metal! 🪚"
        },
        {
          type: "mc",
          q: "PLUMBING: 'My sink was leaking. I used a ___ to tighten the pipes.'",
          options: ["wrench", "hammer", "scissors"],
          a: "wrench",
          tip: "Wrench = llave inglesa! For nuts and bolts!",
          successtip: "✓ WRENCH! Llave inglesa! Plumbing essential! 🔧💧"
        },
        {
          type: "order",
          q: "Traduce: Necesité un taladro y un destornillador.",
          words: ["I", "needed", "a", "drill", "and", "a", "screwdriver"],
          a: "I needed a drill and a screwdriver",
          successtip: "✓ Two tools! Needed (past simple)! 🔩🪛"
        },
        {
          type: "match",
          q: "Tool categories:",
          pairs: [
            { left: "Cutting tools", right: "Scissors, saw, knife" },
            { left: "Fastening tools", right: "Hammer, screwdriver, drill" },
            { left: "Grip tools", right: "Wrench, pliers" }
          ],
          a: "completed",
          successtip: "✓ Organized by function! Easier to remember! 🛠️"
        }
      ]
    },

    // ===================================================================
    // SET 2: USING TOOLS (BASIC) (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u8-s2",
      title: "Set 2: What Did You Use?",
      questions: [
        {
          type: "match",
          q: "Match actions to tools:",
          pairs: [
            { left: "I used a hammer", right: "To hit nails" },
            { left: "I used scissors", right: "To cut paper" },
            { left: "I used a drill", right: "To make holes" }
          ],
          a: "completed",
          successtip: "✓ Use + tool + to + action! Pattern! 🔨✂️🔩"
        },
        {
          type: "mc",
          q: "Yesterday I ___ a hammer to fix my chair.",
          options: ["used", "use", "using"],
          a: "used",
          tip: "Past simple! Use→used (regular verb)!",
          successtip: "✓ USED! Past simple! Use→used! 🔨🪑"
        },
        {
          type: "input",
          q: "QUESTION: 'What did you ___ to open the bottle?' 'I used a bottle opener.'",
          a: "use",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "After 'did', use base form!",
          successtip: "✓ USE! 'What did you use' = ¿Qué usaste? 🍾"
        },
        {
          type: "order",
          q: "Traduce: ¿Usaste un taladro o un destornillador?",
          words: ["Did", "you", "use", "a", "drill", "or", "a", "screwdriver?"],
          a: "Did you use a drill or a screwdriver?",
          successtip: "✓ Question with 'or'! Two options! 🔩🪛"
        },
        {
          type: "mc",
          q: "DIALOGUE: A: 'How did you hang the mirror?' B: 'I ___ a drill to make holes, then I ___ a screwdriver.'",
          options: ["used / used", "use / use", "used / use"],
          a: "used / used",
          successtip: "✓ Both USED! Two tools, two past actions! 🔩🪛🪞"
        },
        {
          type: "input",
          q: "I ___ to cut the wood, but I couldn't find one. (saw)",
          a: "needed a saw",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["needed", "wanted a saw", "wanted"] },
          tip: "Need→needed! Couldn't find = frustration!",
          successtip: "✓ NEEDED! But couldn't find! Common problem! 🪚❌"
        },
        {
          type: "match",
          q: "Common tool expressions:",
          pairs: [
            { left: "I used X to do Y", right: "Purpose" },
            { left: "I needed X but...", right: "Problem" },
            { left: "I couldn't find X", right: "Missing tool" }
          ],
          a: "completed",
          successtip: "✓ Story patterns! Used/needed/couldn't find! 🛠️"
        },
        {
          type: "mc",
          q: "NEGATIVE: 'I wanted to fix it, but I ___ have the right tools.'",
          options: ["didn't", "don't", "wasn't"],
          a: "didn't",
          tip: "Past simple negative! Didn't have!",
          successtip: "✓ DIDN'T! Past negative! Didn't have = no tenía! ❌"
        },
        {
          type: "input",
          q: "COMPLETE: 'I borrowed a ___ from my neighbor to fix my bike.' (tool for tightening)",
          a: "wrench",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["tool", "screwdriver"] },
          successtip: "✓ WRENCH! Borrow from neighbors = classic! 🔧👥"
        },
        {
          type: "order",
          q: "Traduce: No tenía un martillo así que usé una piedra.",
          words: ["I", "didn't", "have", "a", "hammer", "so", "I", "used", "a", "rock"],
          a: "I didn't have a hammer so I used a rock",
          successtip: "✓ Improvisation! Didn't have → used alternative! Creative! 🪨🔨😂"
        }
      ]
    },

    // ===================================================================
    // SET 3: IKEA NIGHTMARE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u8-s3",
      title: "Set 3: Furniture Assembly Fails",
      questions: [
        {
          type: "match",
          q: "Match IKEA vocabulary:",
          pairs: [
            { left: "Instructions", right: "Step-by-step manual" },
            { left: "Allen key/hex key", right: "L-shaped tool (IKEA classic!)" },
            { left: "Parts", right: "Pieces to assemble" }
          ],
          a: "completed",
          tip: "Allen key = llave Allen! IKEA's signature tool!",
          successtip: "✓ IKEA essentials! Instructions 📄, Allen key 🔧, Parts 🧩"
        },
        {
          type: "mc",
          q: "IKEA STORY: 'I bought a bookshelf yesterday. I opened the box and saw 100 ___ everywhere!'",
          options: ["parts", "tools", "instructions"],
          a: "parts",
          successtip: "✓ PARTS! 100 pieces = IKEA reality! 📦🧩"
        },
        {
          type: "input",
          q: "FRUSTRATION: 'I couldn't understand the ___. The pictures were confusing!' (manual)",
          a: "instructions",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["manual", "directions"] },
          successtip: "✓ INSTRUCTIONS! IKEA manuals = picture puzzles! 📄🤔"
        },
        {
          type: "order",
          q: "Traduce: Pasé 4 horas ensamblando un escritorio de IKEA.",
          words: ["I", "spent", "4", "hours", "assembling", "an", "IKEA", "desk"],
          a: "I spent 4 hours assembling an IKEA desk",
          tip: "Spend→spent (irregular)! Assemble = ensamblar!",
          successtip: "✓ SPENT! Spend→spent! 4 hours = normal IKEA time! 🪑⏰😅"
        },
        {
          type: "mc",
          q: "TOOLS: 'IKEA furniture usually comes with a small ___. It's the L-shaped tool.'",
          options: ["Allen key", "hammer", "saw"],
          a: "Allen key",
          tip: "Allen key = hex key! IKEA's signature tool!",
          successtip: "✓ ALLEN KEY! Llave Allen! IKEA's most famous tool! 🔧"
        },
        {
          type: "input",
          q: "MISTAKE: 'I built the whole thing, then realized I ___ (use) the wrong screws! I had to take it apart!'",
          a: "used",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["had used"] },
          successtip: "✓ USED! Classic mistake! Wrong parts = start over! 🔩😭"
        },
        {
          type: "match",
          q: "Assembly story timeline:",
          pairs: [
            { left: "Opened box", right: "Step 1" },
            { left: "Read instructions", right: "Step 2" },
            { left: "Started assembling", right: "Step 3" }
          ],
          a: "completed",
          successtip: "✓ Logical order! But reality = chaos! 📦➡️📄➡️🔧"
        },
        {
          type: "mc",
          q: "COMPLETE STORY: 'I ___ a desk from IKEA. I ___ the instructions. Then I ___ for 3 hours!'",
          options: ["bought / read / worked", "buy / read / work", "bought / read / work"],
          a: "bought / read / worked",
          tip: "Buy→bought, read→read (same spelling!), work→worked!",
          successtip: "✓ Three past verbs! Bought, read, worked! 🛒📖🔨"
        },
        {
          type: "input",
          q: "SUCCESS: 'After 5 hours, I finally ___ it! I felt so proud!' (finish)",
          a: "finished",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ FINISHED! Victory feeling! Worth the struggle! 🏆😊"
        },
        {
          type: "order",
          q: "Traduce: Armé los muebles sin mirar las instrucciones. Fue un error.",
          words: ["I", "assembled", "the", "furniture", "without", "reading", "the", "instructions.", "It", "was", "a", "mistake"],
          a: "I assembled the furniture without reading the instructions. It was a mistake",
          successtip: "✓ Pride before the fall! 'Without reading' = sin leer! Instructions exist for a reason! 📄❌"
        }
      ]
    },

    // ===================================================================
    // SET 4: FASTENERS & MATERIALS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u8-s4",
      title: "Set 4: Nails, Screws, and More",
      questions: [
        {
          type: "match",
          q: "Match fasteners to uses:",
          pairs: [
            { left: "Nails", right: "Use with hammer" },
            { left: "Screws", right: "Use with screwdriver" },
            { left: "Bolts", right: "Use with wrench + nut" }
          ],
          a: "completed",
          tip: "Nails = clavos, Screws = tornillos, Bolts = pernos!",
          successtip: "✓ Three fasteners! Nails 🔨, Screws 🪛, Bolts 🔧"
        },
        {
          type: "mc",
          q: "DIY PROJECT: 'I used a hammer and ___ to attach the wood pieces together.'",
          options: ["nails", "screws", "bolts"],
          a: "nails",
          tip: "Nails go with hammers!",
          successtip: "✓ NAILS! Hammer + nails = classic combo! 🔨💥"
        },
        {
          type: "input",
          q: "ASSEMBLY: 'I needed to tighten the ___ with a screwdriver.' (fastener)",
          a: "screws",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["screw"] },
          successtip: "✓ SCREWS! Tornillos! Tighten = apretar! 🪛"
        },
        {
          type: "order",
          q: "Traduce: Los tornillos eran demasiado grandes para los agujeros.",
          words: ["The", "screws", "were", "too", "big", "for", "the", "holes"],
          a: "The screws were too big for the holes",
          successtip: "✓ Common problem! Wrong size! Too big/small! 🔩❌"
        },
        {
          type: "match",
          q: "Materials:",
          pairs: [
            { left: "Wood", right: "Natural material" },
            { left: "Metal", right: "Strong material" },
            { left: "Plastic", right: "Lightweight material" }
          ],
          a: "completed",
          tip: "Wood = madera, Metal = metal, Plastic = plástico!",
          successtip: "✓ Three materials! Wood 🪵, Metal 🔩, Plastic 🧴"
        },
        {
          type: "mc",
          q: "PROJECT: 'I built a shelf out of ___. It was cheaper than buying one.'",
          options: ["wood", "metal", "plastic"],
          a: "wood",
          successtip: "✓ WOOD! Madera! DIY furniture material! 🪵📚"
        },
        {
          type: "input",
          q: "PROBLEM: 'The ___ broke when I tried to hammer it in. It was too thin!' (fastener)",
          a: "nail",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["nails", "screw"] },
          successtip: "✓ NAIL! Cheap nails break! Quality matters! 🔨💔"
        },
        {
          type: "mc",
          q: "PAINTING: 'I painted my room. I needed ___, a roller, and tape.'",
          options: ["paint", "wood", "nails"],
          a: "paint",
          tip: "Paint = pintura! For walls!",
          successtip: "✓ PAINT! Pintura! Painting supplies! 🎨🖌️"
        },
        {
          type: "input",
          q: "MEASURE: 'I used a tape ___ to check if the shelf would fit.' (measuring tool)",
          a: "measure",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["ruler"] },
          tip: "Tape measure = cinta métrica!",
          successtip: "✓ TAPE MEASURE! Cinta métrica! Measure twice, cut once! 📏"
        },
        {
          type: "order",
          q: "Traduce: Necesitaba pegamento pero solo tenía cinta adhesiva.",
          words: ["I", "needed", "glue", "but", "I", "only", "had", "tape"],
          a: "I needed glue but I only had tape",
          tip: "Glue = pegamento, Tape = cinta adhesiva!",
          successtip: "✓ Making do! Glue vs tape! Sometimes tape works! 📦✨"
        }
      ]
    },

    // ===================================================================
    // SET 5: HOME REPAIRS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u8-s5",
      title: "Set 5: Fixing Things Around the House",
      questions: [
        {
          type: "match",
          q: "Match problems to solutions:",
          pairs: [
            { left: "Leaking sink", right: "Use wrench to tighten" },
            { left: "Broken chair", right: "Use glue or nails" },
            { left: "Loose door handle", right: "Tighten screws" }
          ],
          a: "completed",
          successtip: "✓ Common home problems + fixes! 🔧🪑🚪"
        },
        {
          type: "mc",
          q: "PLUMBING: 'My sink was dripping all night. I ___ a YouTube tutorial and ___ a wrench to fix it!'",
          options: ["watched / used", "watch / use", "watched / use"],
          a: "watched / used",
          tip: "Watch→watched, use→used! Both past simple!",
          successtip: "✓ WATCHED + USED! YouTube tutorials = modern repair guide! 🎥🔧"
        },
        {
          type: "input",
          q: "FURNITURE: 'One leg of my chair broke. I ___ (fix) it with wood glue and it worked!'",
          a: "fixed",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["repaired"] },
          tip: "Fix→fixed (regular)!",
          successtip: "✓ FIXED! Fix→fixed! Wood glue saves furniture! 🪑🧴"
        },
        {
          type: "order",
          q: "Traduce: Apreté los tornillos flojos de la puerta.",
          words: ["I", "tightened", "the", "loose", "screws", "on", "the", "door"],
          a: "I tightened the loose screws on the door",
          tip: "Tighten = apretar, Loose = flojo/suelto!",
          successtip: "✓ TIGHTENED! Tighten (regular verb)! Loose screws = common issue! 🔩🚪"
        },
        {
          type: "mc",
          q: "COULD/COULDN'T: 'I tried to fix the TV, but I ___ open the back without the right screwdriver.'",
          options: ["couldn't", "could", "can't"],
          a: "couldn't",
          successtip: "✓ COULDN'T! Needed special tool! Security screws! 📺🔒"
        },
        {
          type: "input",
          q: "SUCCESS STORY: 'My bathroom door wouldn't close properly. I adjusted the ___ with a screwdriver and now it works perfectly!'",
          a: "hinges",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["hinge", "screws", "handle"] },
          tip: "Hinges = bisagras! The metal parts doors swing on!",
          successtip: "✓ HINGES! Bisagras! Simple fix, big difference! 🚪✨"
        },
        {
          type: "match",
          q: "Repair verbs:",
          pairs: [
            { left: "Fix/repair", right: "Make it work again" },
            { left: "Tighten", right: "Make it tight/secure" },
            { left: "Replace", right: "Put a new one" }
          ],
          a: "completed",
          successtip: "✓ Fix, tighten, replace! Key repair verbs! 🔧"
        },
        {
          type: "mc",
          q: "GAVE UP: 'I tried to fix the washing machine, but it was too complicated. I finally ___ a professional.'",
          options: ["called", "call", "calling"],
          a: "called",
          tip: "Call→called! Sometimes you need an expert!",
          successtip: "✓ CALLED! Know your limits! Professionals exist for a reason! 📞💧"
        },
        {
          type: "input",
          q: "PROUD MOMENT: 'I ___ my bike chain by myself. I felt like a mechanic!' (fix)",
          a: "fixed",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["repaired", "replaced"] },
          successtip: "✓ FIXED! Small repairs = big confidence! 🚲💪"
        },
        {
          type: "order",
          q: "Traduce: No pude arreglarlo así que lo llevé a un profesional.",
          words: ["I", "couldn't", "fix", "it", "so", "I", "took", "it", "to", "a", "professional"],
          a: "I couldn't fix it so I took it to a professional",
          tip: "Take→took (irregular)!",
          successtip: "✓ COULDN'T + TOOK! Knowing when to ask for help! 🙏🔧"
        }
      ]
    },

    // ===================================================================
    // SET 6: DIY DISASTERS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u8-s6",
      title: "Set 6: When DIY Goes Wrong",
      questions: [
        {
          type: "match",
          q: "Match disasters to consequences:",
          pairs: [
            { left: "Used wrong drill bit", right: "Hole too big" },
            { left: "Didn't measure", right: "Pieces don't fit" },
            { left: "Forgot to turn off water", right: "Flooded bathroom" }
          ],
          a: "completed",
          successtip: "✓ Learn from mistakes! Measure twice, drill once! 🔩💦"
        },
        {
          type: "mc",
          q: "FAIL: 'I tried to hang a heavy mirror. I used tiny nails and it ___ down after 5 minutes!'",
          options: ["fell", "fall", "falled"],
          a: "fell",
          tip: "Fall→fell (irregular)!",
          successtip: "✓ FELL! Fall→fell! Wrong nails = gravity wins! 🪞💥"
        },
        {
          type: "input",
          q: "OOPS: 'I ___ (paint) the walls but I forgot to put tape on the edges. Paint got everywhere!'",
          a: "painted",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Paint→painted (regular)! Painter's tape = essential!",
          successtip: "✓ PAINTED! Always tape first! Messy edges! 🎨😅"
        },
        {
          type: "order",
          q: "Traduce: Rompí el taladro porque lo usé incorrectamente.",
          words: ["I", "broke", "the", "drill", "because", "I", "used", "it", "incorrectly"],
          a: "I broke the drill because I used it incorrectly",
          tip: "Break→broke (irregular)!",
          successtip: "✓ BROKE! Break→broke! Read the manual first! 🔩💔"
        },
        {
          type: "mc",
          q: "MEASURE TWICE: 'I cut the wood, but I ___ the measurements. It was 5cm too short!'",
          options: ["messed up", "measured", "cut"],
          a: "messed up",
          tip: "'Messed up' = cometí un error! Informal expression!",
          successtip: "✓ MESSED UP! Measure twice, cut once! Classic mistake! 📏🪚😭"
        },
        {
          type: "input",
          q: "LEARNING MOMENT: 'I tried to fix my laptop with a tiny screwdriver. I ___ (strip) the screw and made it worse!'",
          a: "stripped",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["damaged", "broke"] },
          tip: "Strip a screw = damage the grooves so it won't turn!",
          successtip: "✓ STRIPPED! Wrong tool = damaged screw! Now it's unfixable! 💻🔩😱"
        },
        {
          type: "match",
          q: "Common DIY mistakes:",
          pairs: [
            { left: "Skipped instructions", right: "Built wrong" },
            { left: "Used wrong tool", right: "Broke something" },
            { left: "Rushed the job", right: "Poor quality result" }
          ],
          a: "completed",
          successtip: "✓ Learn from errors! Patience + right tools = success! 🛠️"
        },
        {
          type: "mc",
          q: "CONFESSION: 'I ___ to build a table, but it came out crooked. I had to take it apart and start over.'",
          options: ["tried", "try", "trying"],
          a: "tried",
          tip: "Try→tried! Change y to i + ed!",
          successtip: "✓ TRIED! Try→tried! Starting over = frustrating but necessary! 🪵😤"
        },
        {
          type: "input",
          q: "HOLE DISASTER: 'I drilled a hole in the wrong place and ___ (damage) the wall. I had to patch it up!'",
          a: "damaged",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["broke", "ruined"] },
          successtip: "✓ DAMAGED! Wrong spot! Patch = reparar/rellenar! 🕳️🧱"
        },
        {
          type: "order",
          q: "Traduce: Traté de ahorrar dinero pero terminé gastando más en arreglar mis errores.",
          words: ["I", "tried", "to", "save", "money", "but", "ended", "up", "spending", "more", "fixing", "my", "mistakes"],
          a: "I tried to save money but ended up spending more fixing my mistakes",
          successtip: "✓ DIY irony! Tried to save → spent more! Sometimes professionals are cheaper! 💸😅"
        }
      ]
    },

    // ===================================================================
    // SET 7: SUCCESSFUL PROJECTS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u8-s7",
      title: "Set 7: DIY Success Stories",
      questions: [
        {
          type: "match",
          q: "Match projects to tools used:",
          pairs: [
            { left: "Built a bookshelf", right: "Saw, drill, screws" },
            { left: "Painted a room", right: "Brush, roller, tape" },
            { left: "Fixed bike", right: "Wrench, pump, oil" }
          ],
          a: "completed",
          successtip: "✓ Different projects = different tools! 📚🎨🚲"
        },
        {
          type: "mc",
          q: "SUCCESS: 'I built a small table for my balcony. I ___ wood, ___ it to size, and ___ it together with screws!'",
          options: ["bought / cut / put", "buy / cut / put", "bought / cutted / putted"],
          a: "bought / cut / put",
          tip: "Buy→bought, cut→cut (same!), put→put (same!)!",
          successtip: "✓ Three verbs! Bought, cut (no change!), put (no change!)! 🪵🪚✨"
        },
        {
          type: "input",
          q: "PAINTING: 'Last weekend I ___ (paint) my bedroom. It took two days but it looks amazing now!'",
          a: "painted",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ PAINTED! Fresh paint = new room feeling! 🎨🖌️😊"
        },
        {
          type: "order",
          q: "Traduce: Construí una mesa de café usando madera reciclada.",
          words: ["I", "built", "a", "coffee", "table", "using", "recycled", "wood"],
          a: "I built a coffee table using recycled wood",
          tip: "Build→built (irregular)!",
          successtip: "✓ BUILT! Build→built! Eco-friendly DIY! ♻️☕🪵"
        },
        {
          type: "mc",
          q: "GARDEN PROJECT: 'I ___ a raised garden bed. I used wood planks and screws. Now I can grow vegetables!'",
          options: ["made", "make", "maked"],
          a: "made",
          tip: "Make→made (irregular)!",
          successtip: "✓ MADE! Make→made! Gardening DIY! 🌱🪵🥕"
        },
        {
          type: "input",
          q: "ORGANIZATION: 'I installed shelves in my closet using a drill and ___ (screw). Now everything has a place!'",
          a: "screws",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["brackets", "anchors"] },
          successtip: "✓ SCREWS! Organization project! Closet upgrade! 🗄️✨"
        },
        {
          type: "match",
          q: "Project stages:",
          pairs: [
            { left: "Planning", right: "Measure, design, buy materials" },
            { left: "Building", right: "Cut, drill, assemble" },
            { left: "Finishing", right: "Sand, paint, enjoy!" }
          ],
          a: "completed",
          successtip: "✓ Three stages! Plan → Build → Finish! 📋🔨🎨"
        },
        {
          type: "mc",
          q: "DECOR: 'I ___ old furniture from a thrift store and ___ it. Now it looks brand new!'",
          options: ["bought / refinished", "buy / refinish", "bought / refinish"],
          a: "bought / refinished",
          tip: "Refinish = restore/repaint old furniture! Popular DIY!",
          successtip: "✓ BOUGHT + REFINISHED! Thrift flip! Old → new! 🪑✨"
        },
        {
          type: "input",
          q: "LIGHTING: 'I hung a new light fixture using a ladder, drill, and ___. It changed the whole room!' (fastener)",
          a: "screws",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["bolts", "anchors"] },
          successtip: "✓ SCREWS! Lighting upgrade! Big visual impact! 💡✨"
        },
        {
          type: "order",
          q: "Traduce: Invertí tiempo y esfuerzo pero valió totalmente la pena.",
          words: ["I", "invested", "time", "and", "effort", "but", "it", "was", "totally", "worth", "it"],
          a: "I invested time and effort but it was totally worth it",
          successtip: "✓ Pride of completion! DIY satisfaction! Time well spent! 💪😊"
        }
      ]
    },

    // ===================================================================
    // SET 8: LEARNING TO USE TOOLS (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u8-s8",
      title: "Set 8: First Time Using Tools",
      questions: [
        {
          type: "match",
          q: "Match learning experiences:",
          pairs: [
            { left: "First time with drill", right: "Nervous but excited" },
            { left: "Learning from mistakes", right: "Best teacher" },
            { left: "YouTube tutorials", right: "Modern learning method" }
          ],
          a: "completed",
          successtip: "✓ Everyone starts somewhere! Practice makes perfect! 🔩📱"
        },
        {
          type: "mc",
          q: "FIRST TIME: 'I had never used a power drill before. I ___ a tutorial and tried it. It was easier than I thought!'",
          options: ["watched", "watch", "watching"],
          a: "watched",
          successtip: "✓ WATCHED! YouTube = DIY university! 🎥🔩"
        },
        {
          type: "input",
          q: "LEARNING: 'My dad ___ (teach) me how to use a saw safely when I was a teenager.'",
          a: "taught",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Teach→taught (irregular)!",
          successtip: "✓ TAUGHT! Teach→taught! Family skills! Safety first! 🪚👨‍👦"
        },
        {
          type: "order",
          q: "Traduce: Aprendí a usar herramientas viendo videos en YouTube.",
          words: ["I", "learned", "to", "use", "tools", "by", "watching", "YouTube", "videos"],
          a: "I learned to use tools by watching YouTube videos",
          tip: "Learn→learned (regular in US, learnt in UK)!",
          successtip: "✓ LEARNED! Modern education! YouTube DIY channel generation! 📱🔨"
        },
        {
          type: "mc",
          q: "CONFIDENCE: 'At first I ___ scared of power tools. Now I use them all the time!'",
          options: ["was", "am", "were"],
          a: "was",
          tip: "Past: was/were! 'Was scared' = tenía miedo!",
          successtip: "✓ WAS! Fear → confidence! Growth! 🔩😊"
        },
        {
          type: "input",
          q: "PRACTICE: 'I ___ (practice) on scrap wood before starting my real project. Smart decision!'",
          a: "practiced",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["practised"] },
          successtip: "✓ PRACTICED! Practice on scraps = no wasted materials! 🪵✅"
        },
        {
          type: "match",
          q: "Safety vocabulary:",
          pairs: [
            { left: "Safety goggles", right: "Eye protection" },
            { left: "Gloves", right: "Hand protection" },
            { left: "Ear protection", right: "For loud tools" }
          ],
          a: "completed",
          tip: "Safety first! Goggles = gafas de seguridad!",
          successtip: "✓ Safety gear! Protect yourself! 👓🧤👂"
        },
        {
          type: "mc",
          q: "SAFETY: 'I always wear safety ___ when using the drill. I don't want sawdust in my eyes!'",
          options: ["goggles", "gloves", "shoes"],
          a: "goggles",
          tip: "Goggles = gafas de seguridad!",
          successtip: "✓ GOGGLES! Eye protection = essential! Safety first! 👓🔩"
        },
        {
          type: "input",
          q: "IMPROVEMENT: 'The first shelf I built was crooked. But each project ___ (get) better!'",
          a: "got",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["gets"] },
          tip: "Get→got! 'Got better' = mejoraba!",
          successtip: "✓ GOT! Get→got! Improvement with practice! 📈🔨"
        },
        {
          type: "order",
          q: "Traduce: Cometí muchos errores al principio pero aprendí de cada uno.",
          words: ["I", "made", "many", "mistakes", "at", "first", "but", "I", "learned", "from", "each", "one"],
          a: "I made many mistakes at first but I learned from each one",
          tip: "Make→made (irregular)!",
          successtip: "✓ MADE + LEARNED! Mistakes = learning opportunities! Growth mindset! 💪📚"
        }
      ]
    },

    // ===================================================================
    // SET 9: CAR & BIKE MAINTENANCE (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u8-s9",
      title: "Set 9: Vehicle Maintenance",
      questions: [
        {
          type: "match",
          q: "Match vehicle tools:",
          pairs: [
            { left: "Wrench set", right: "For bolts/nuts" },
            { left: "Tire pump", right: "Inflate tires" },
            { left: "Jack", right: "Lift car" }
          ],
          a: "completed",
          tip: "Jack = gato (para levantar el coche)!",
          successtip: "✓ Vehicle essentials! Wrench 🔧, Pump 🚲, Jack 🚗"
        },
        {
          type: "mc",
          q: "BIKE REPAIR: 'My bike tire was flat. I used a ___ to inflate it.'",
          options: ["pump", "wrench", "drill"],
          a: "pump",
          tip: "Pump = bomba! For tires!",
          successtip: "✓ PUMP! Bike pump = essential cyclist tool! 🚲💨"
        },
        {
          type: "input",
          q: "CAR BASIC: 'I ___ (change) my car's oil by myself. I saved $50!'",
          a: "changed",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Change→changed (regular)!",
          successtip: "✓ CHANGED! DIY car maintenance saves money! 🚗💰"
        },
        {
          type: "order",
          q: "Traduce: Reemplacé mi llanta pinchada usando un gato y una llave.",
          words: ["I", "replaced", "my", "flat", "tire", "using", "a", "jack", "and", "a", "wrench"],
          a: "I replaced my flat tire using a jack and a wrench",
          tip: "Flat tire = llanta pinchada!",
          successtip: "✓ REPLACED! Jack lifts, wrench removes bolts! Essential skill! 🚗🔧"
        },
        {
          type: "mc",
          q: "CHAIN REPAIR: 'My bike chain came off. I ___ it back on and ___ it with oil.'",
          options: ["put / lubricated", "put / lubricate", "putted / lubricated"],
          a: "put / lubricated",
          tip: "Put→put (no change)! Lubricate = lubricar!",
          successtip: "✓ PUT + LUBRICATED! Chain maintenance! 🚲⛓️"
        },
        {
          type: "input",
          q: "FLAT TIRE: 'I got a flat tire on the highway. Luckily I had a spare tire and ___.' (lifting tool)",
          a: "jack",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["a jack"] },
          successtip: "✓ JACK! Always keep one in your car! Roadside essential! 🚗🛞"
        },
        {
          type: "match",
          q: "Maintenance verbs:",
          pairs: [
            { left: "Change oil", right: "Every 5,000 miles" },
            { left: "Check tire pressure", right: "Monthly" },
            { left: "Replace brake pads", right: "When worn" }
          ],
          a: "completed",
          successtip: "✓ Regular maintenance = vehicle longevity! 🚗⏰"
        },
        {
          type: "mc",
          q: "LEARNING: 'I didn't know how to change a tire, so I ___ a video and practiced. Now I can do it in 10 minutes!'",
          options: ["watched", "watch", "watching"],
          a: "watched",
          successtip: "✓ WATCHED! YouTube mechanic! Important life skill! 🎥🔧"
        },
        {
          type: "input",
          q: "BRAKE CHECK: 'I ___ (check) my brake pads last week. They were worn out, so I replaced them.'",
          a: "checked",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ CHECKED! Regular checks = safety! Brakes = critical! 🚗🛑"
        },
        {
          type: "order",
          q: "Traduce: Aprendí mantenimiento básico de carro para ahorrar dinero en el mecánico.",
          words: ["I", "learned", "basic", "car", "maintenance", "to", "save", "money", "on", "the", "mechanic"],
          a: "I learned basic car maintenance to save money on the mechanic",
          successtip: "✓ LEARNED! Financial motivation! Basic skills = big savings! 💰🚗"
        }
      ]
    },

    // ===================================================================
    // SET 10: TOOL SHOPPING & RECOMMENDATIONS (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u8-s10",
      title: "Set 10: Building Your Toolkit",
      questions: [
        {
          type: "match",
          q: "Match toolkit levels:",
          pairs: [
            { left: "Basic toolkit", right: "Hammer, screwdriver, wrench" },
            { left: "Intermediate", right: "Drill, saw, level" },
            { left: "Advanced", right: "Power tools, specialty items" }
          ],
          a: "completed",
          successtip: "✓ Build toolkit gradually! Start basic! 🔨➡️🔩➡️⚡"
        },
        {
          type: "mc",
          q: "SHOPPING: 'I ___ my first toolkit last month. It came with all the basics!'",
          options: ["bought", "buy", "buyed"],
          a: "bought",
          tip: "Buy→bought!",
          successtip: "✓ BOUGHT! Starter kits = good investment! 🛠️💰"
        },
        {
          type: "input",
          q: "QUALITY: 'I bought cheap tools at first. They ___ (break) quickly. Now I buy better quality!'",
          a: "broke",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Break→broke! Quality matters!",
          successtip: "✓ BROKE! Buy cheap, buy twice! Invest in quality! 🔨💔➡️🔨✅"
        },
        {
          type: "order",
          q: "Traduce: Invertí en un buen taladro eléctrico y no me arrepiento.",
          words: ["I", "invested", "in", "a", "good", "electric", "drill", "and", "I", "don't", "regret", "it"],
          a: "I invested in a good electric drill and I don't regret it",
          successtip: "✓ INVESTED! Quality tools = long-term savings! 🔩💰"
        },
        {
          type: "mc",
          q: "RECOMMENDATION: 'If you only buy one power tool, get a ___. It's the most versatile!'",
          options: ["drill", "saw", "sander"],
          a: "drill",
          tip: "Drill = most useful first power tool!",
          successtip: "✓ DRILL! Drill first, others later! Most versatile! 🔩#1"
        },
        {
          type: "input",
          q: "BORROWING: 'I didn't want to buy a ladder for one project, so I ___ (borrow) my neighbor's.'",
          a: "borrowed",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ BORROWED! Neighbor tool library! Community! 👥🪜"
        },
        {
          type: "match",
          q: "Shopping tips:",
          pairs: [
            { left: "Read reviews", right: "Check quality" },
            { left: "Compare prices", right: "Find deals" },
            { left: "Buy what you need", right: "Don't overbuy" }
          ],
          a: "completed",
          successtip: "✓ Smart shopping! Research before buying! 🔍💰"
        },
        {
          type: "mc",
          q: "STORAGE: 'I bought a toolbox to organize everything. Now I can ___ any tool in seconds!'",
          options: ["find", "found", "finding"],
          a: "find",
          tip: "Present! 'Now I can' = ability now!",
          successtip: "✓ FIND! Organization = efficiency! Toolbox essential! 🧰✨"
        },
        {
          type: "input",
          q: "UPGRADE: 'I started with manual tools. Last year I ___ (upgrade) to power tools. Huge difference!'",
          a: "upgraded",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ UPGRADED! Manual → power = game changer! ⚡🔧"
        },
        {
          type: "order",
          q: "FINAL: Traduce: Si pudieras darme un consejo, ¿qué herramienta debería comprar primero?",
          words: ["If", "you", "could", "give", "me", "advice,", "which", "tool", "should", "I", "buy", "first?"],
          a: "If you could give me advice, which tool should I buy first?",
          successtip: "✓ 🎉 PERFECTO! Asking for recommendations! You can now talk about tools, repairs, DIY projects, and vehicle maintenance! Tool master! 🏆🛠️"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - 3 PARAGRAPHS (10 questions)
  // ===================================================================
 // ===================================================================
// READING SET: My First DIY Project
// ===================================================================
readingSet: {
  id: "m5-u8-reading",
  title: "Reading: My First DIY Project",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Preparation & Shopping (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**A Plan is Born**

Last spring I decided to build a bookshelf for my apartment. I was tired of keeping my books in boxes. I watched YouTube tutorials for weeks and made a plan. 

I went to the hardware store and bought wood, screws, wood glue, sandpaper, and paint. I also bought my first power drill! I was nervous but excited. The total cost was $80—way cheaper than buying a finished bookshelf.`
    },
    {
      type: "mc",
      q: "Why did the writer start this project?",
      options: ["Tired of books in boxes", "Hobby", "Gift for friend"],
      a: "Tired of books in boxes",
      successtip: "✓ Practical motivation! Books in boxes = need solution! 📚📦"
    },
    {
      type: "input",
      q: "Where did they buy the materials?",
      a: "hardware store",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["hardware", "store"] },
      successtip: "✓ HARDWARE STORE! Ferretería! DIY shopping! 🛠️🏪"
    },
    {
      type: "mc",
      q: "How much did the materials cost in total?",
      options: ["$80", "$50", "$100"],
      a: "$80",
      successtip: "✓ $80! Cheaper than buying finished! 💰"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: The Disaster (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Day One: Mistakes**

The first day was a disaster. I measured the wood wrong and cut one piece too short. I had to go back to the store and buy more wood. Then I used the drill for the first time and accidentally made holes in the wrong spots. 

I felt frustrated and almost gave up. But I remembered: mistakes are part of learning. I took a break, had coffee, and started again more carefully.`
    },
    {
      type: "mc",
      q: "What was the very first thing that went wrong?",
      options: ["Measured wrong", "Drill broke", "Lost instructions"],
      a: "Measured wrong",
      successtip: "✓ Classic mistake! Measure twice, cut once! 📏❌"
    },
    {
      type: "input",
      q: "How did the writer feel after the mistakes?",
      a: "frustrated",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["frustrated and almost gave up", "upset", "discouraged"] },
      successtip: "✓ FRUSTRATED! Normal DIY emotion! 😤"
    },
    {
      type: "mc",
      q: "What did they do to reset before trying again?",
      options: ["Took a break and had coffee", "Called for help", "Quit"],
      a: "Took a break and had coffee",
      successtip: "✓ Smart! Breaks help! Coffee = reset! ☕🔄"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Success & Pride (preguntas 7-10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Day Two: Success**

The second day went much better. I measured carefully, cut the wood correctly, and drilled precise holes. I assembled the pieces using screws and wood glue. Then I sanded the rough edges to make them smooth. Finally, I painted it white. 

It took two days total, but I did it! My bookshelf isn't perfect—it's slightly crooked—but I built it with my own hands. Now every time I look at it, I feel proud. Plus, I learned skills I'll use forever!`
    },
    {
      type: "mc",
      q: "How long did the full project take?",
      options: ["Two days", "One day", "One week"],
      a: "Two days",
      successtip: "✓ TWO DAYS! Day 1 = mistakes, Day 2 = success! 📅"
    },
    {
      type: "input",
      q: "What color did they choose for the bookshelf?",
      a: "white",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ WHITE! Clean finish! 🎨⚪"
    },
    {
      type: "mc",
      q: "Is the final bookshelf perfect?",
      options: ["No, it's slightly crooked", "Yes, it is perfect", "The writer didn't finish it"],
      a: "No, it's slightly crooked",
      successtip: "✓ Slightly crooked but finished! Perfection not required for pride! 📚"
    },
    {
      type: "mc",
      q: "COMPREHENSION: What is the main message of the story?",
      options: ["Mistakes lead to learning and pride", "Everything should go perfectly", "Don't try DIY projects"],
      a: "Mistakes lead to learning and pride",
      successtip: "✓ Perfect! DIY journey: Fail → Learn → Try again → Succeed! Growth mindset! 🌱🔨🏆"
    }
  ]
},

  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u8-review",
    title: "Unit 8 Review: Tools & DIY Mastery",
    questions: [
      {
        type: "match",
        q: "Review basic tools:",
        pairs: [
          { left: "Hammer", right: "Hit nails" },
          { left: "Screwdriver", right: "Turn screws" },
          { left: "Drill", right: "Make holes" }
        ],
        a: "completed",
        successtip: "✓ Three essentials reviewed! 🔨🪛🔩"
      },
      {
        type: "mc",
        q: "Yesterday I ___ a hammer to hang a picture.",
        options: ["used", "use", "using"],
        a: "used",
        successtip: "✓ USED! Past simple! Use→used! 🔨🖼️"
      },
      {
        type: "input",
        q: "I needed ___ and nails to fix the chair. (hitting tool)",
        a: "hammer",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["a hammer"] },
        successtip: "✓ HAMMER! Hammer + nails = classic combo! 🔨"
      },
      {
        type: "mc",
        q: "The furniture came with an ___ key for assembly.",
        options: ["Allen", "English", "Master"],
        a: "Allen",
        successtip: "✓ ALLEN KEY! IKEA signature tool! 🔧"
      },
      {
        type: "order",
        q: "Make a sentence:",
        words: ["I", "fixed", "my", "bike", "with", "a", "wrench"],
        a: "I fixed my bike with a wrench",
        successtip: "✓ Fixed + with = tool used! 🚲🔧"
      },
      {
        type: "input",
        q: "I couldn't open the box without ___. (cutting tool)",
        a: "scissors",
        accept: { ignoreCase: true, ignorePunctuation: true, alts: ["a knife", "knife"] },
        successtip: "✓ SCISSORS! Couldn't without = needed! ✂️"
      },
      {
        type: "mc",
        q: "What holds pieces together? A) Hammer B) Screws C) Drill",
        options: ["B - Screws", "A - Hammer", "C - Drill"],
        a: "B - Screws",
        successtip: "✓ SCREWS! Fasteners! Tornillos! 🔩"
      },
      {
        type: "match",
        q: "Review contexts:",
        pairs: [
          { left: "IKEA assembly", right: "Allen key, instructions" },
          { left: "Home repair", right: "Wrench, screwdriver" },
          { left: "Bike maintenance", right: "Pump, wrench" }
        ],
        a: "completed",
        successtip: "✓ Different contexts! Different tools! 🛠️"
      },
      {
        type: "input",
        q: "I ___ the wood with a saw. (cut)",
        a: "cut",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "Cut→cut (same form!)!",
        successtip: "✓ CUT! Cut→cut (no change)! 🪚"
      },
      {
        type: "mc",
        q: "FINAL STORY: 'Last month I built a bookshelf. I used a drill, saw, and screws. It took two days but I finished it! I felt proud!' This shows:",
        options: ["Complete DIY project with multiple tools", "Bought furniture", "Failed project"],
        a: "Complete DIY project with multiple tools",
        successtip: "✓ 🎉 PERFECTO! Complete DIY story! Multiple tools + past simple + emotions! You can talk about repairs, projects, and tools like a pro! 🏆🛠️"
      }
    ]
  }
};
